import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { useTranslation } from 'react-i18next'

// ── Types ─────────────────────────────────────────────────────────────────────
interface Product {
  name: string
  image: string
  description: string
}

// ── Constants ─────────────────────────────────────────────────────────────────
const BRAND = '#6eb446'
const BRAND_SHADOW = '0 8px 24px rgba(110,180,70,0.35)'

// ── Skeleton card ─────────────────────────────────────────────────────────────
const SkeletonCard: React.FC = () => (
  <div style={{
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minHeight: 280,
  }}>
    <div className="shimmer" style={{ width: 80, height: 80, borderRadius: '50%', margin: '0 auto' }} />
    <div className="shimmer" style={{ height: 20, borderRadius: 6, width: '60%', margin: '0 auto' }} />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className="shimmer" style={{ height: 14, borderRadius: 4 }} />
      <div className="shimmer" style={{ height: 14, borderRadius: 4, width: '85%' }} />
      <div className="shimmer" style={{ height: 14, borderRadius: 4, width: '70%' }} />
    </div>
    <div className="shimmer" style={{ height: 40, borderRadius: 9999, width: '60%', margin: '0 auto' }} />
  </div>
)

// ── Product card ──────────────────────────────────────────────────────────────
interface ProductCardProps {
  product: Product
  url: string
  label: string
}

const ProductCard: React.FC<ProductCardProps> = ({ product, url, label }) => {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: '#fff',
        borderRadius: '16px',
        padding: '28px 24px',
        boxShadow: hovered
          ? '0 16px 48px rgba(0,0,0,0.12)'
          : '0 2px 12px rgba(0,0,0,0.06)',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'box-shadow 0.25s, transform 0.25s',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        cursor: 'default',
        border: `1px solid ${hovered ? '#e2f4d6' : '#f1f5f9'}`,
        minHeight: 280,
      }}
    >
      {/* Image */}
      <div style={{
        width: 90,
        height: 90,
        borderRadius: '50%',
        backgroundColor: '#f4fbf0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        border: `2px solid ${hovered ? '#c8edb0' : '#e8f5dc'}`,
        transition: 'border-color 0.25s',
      }}>
        <img
          src={`${url}images/${product.image}`}
          alt={product.name}
          width={56}
          height={56}
          style={{ objectFit: 'contain' }}
        />
      </div>

      {/* Name */}
      <h3 style={{
        margin: 0,
        fontSize: '1.05rem',
        fontWeight: 700,
        color: '#1e293b',
        textAlign: 'center',
      }}>
        {product.name}
      </h3>

      {/* Description */}
      <p style={{
        margin: 0,
        fontSize: '0.875rem',
        color: '#64748b',
        lineHeight: 1.6,
        textAlign: 'center',
        flex: 1,
      }}>
        {product.description}
      </p>

      {/* CTA */}
      <Link
        to={`/products/${product.name}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '9px 24px',
          borderRadius: '9999px',
          backgroundColor: hovered ? BRAND : 'transparent',
          color: hovered ? '#fff' : BRAND,
          border: `1.5px solid ${BRAND}`,
          fontWeight: 600,
          fontSize: '0.875rem',
          textDecoration: 'none',
          boxShadow: hovered ? BRAND_SHADOW : 'none',
          transition: 'background-color 0.25s, color 0.25s, box-shadow 0.25s',
        }}
      >
        {label}
      </Link>
    </div>
  )
}

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV
// ── Products ──────────────────────────────────────────────────────────────────
const Products: React.FC = () => {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState<Product[]>([])

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get<{ message: Product[] }>(`${url}products/home`)
        setProducts(res.data.message)
      } catch (_) {
        // fail silently — products just won't appear
      } finally {
        setLoading(true)
      }
    }
    fetch()
  }, [])

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position:  400px 0; }
        }
        .shimmer {
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
          background-size: 400px 100%;
          animation: shimmer 1.4s ease-in-out infinite;
        }
      `}</style>

      <section id="products-section" style={{ backgroundColor: '#f8faf6', borderBottom: '4px solid #000', padding: '5rem 1.5rem' }}>

        {/* Grid */}
        <div style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '1.5rem',
        }}>
          {!loading
            ? [0, 1, 2, 3].map(i => <SkeletonCard key={i} />)
            : products.map((p, i) => (
              <ProductCard
                key={i}
                product={p}
                url={url ?? ''}
                label={t('pages.products.view_prices')}
              />
            ))
          }
        </div>
      </section>
    </>
  )
}

export default Products
